import React, { useEffect, useRef } from 'react'
import { FaVideo } from 'react-icons/fa'
import Button from '@renderer/components/bahela/Button'
import { formatAmount, type LocalCurrency } from '@renderer/utils/formatCurrency'

import bgLayer from '/assets/bahela/bg-layer.webp'

interface RecordingChoiceModalProps {
  isVisible: boolean
  recordingPrice: number
  currency: LocalCurrency
  timeout?: number
  onChoice: (includeRecording: boolean) => void
  language: 'id' | 'en'
}

const RecordingChoiceModal: React.FC<RecordingChoiceModalProps> = ({
  isVisible,
  recordingPrice,
  currency,
  timeout = 45,
  onChoice,
  language
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const chosenRef = useRef(false)
  const onChoiceRef = useRef(onChoice)
  onChoiceRef.current = onChoice

  useEffect(() => {
    if (!isVisible || timeout <= 0) return
    chosenRef.current = false

    timeoutRef.current = setTimeout(() => {
      if (!chosenRef.current) onChoiceRef.current(false)
    }, timeout * 1000)

    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isVisible, timeout])

  if (!isVisible) return null

  const handleChoice = (includeRecording: boolean): void => {
    if (chosenRef.current) return
    chosenRef.current = true
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    onChoice(includeRecording)
  }

  const priceLabel = formatAmount(recordingPrice, currency)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-8">
      <div
        className="absolute inset-0 bg-black/90"
        style={{ backgroundImage: `url(${bgLayer})` }}
      />

      <div className="relative bg-methadone/95 backdrop-blur-md rounded-3xl shadow-2xl border-4 border-uriYellow p-10 max-w-3xl w-full">
        <div className="flex flex-col items-center text-center gap-6">
          <div className="flex items-center justify-center w-20 h-20 rounded-full bg-uriYellow shadow-lg">
            <FaVideo size={36} color="#B3336C" />
          </div>

          <h1 className="text-[48px] font-serif-bahela text-uriYellow drop-shadow-2xl leading-tight">
            {language === 'id' ? 'Foto & Rekam' : 'Photo & Video'}
          </h1>

          <p className="text-[28px] text-white font-body-bahela leading-relaxed drop-shadow-lg max-w-2xl">
            {language === 'id'
              ? `Mau sekalian rekam video kenangan? Tambahan ${priceLabel}.`
              : `Add a memory video for an extra ${priceLabel}?`}
          </p>

          <div className="flex gap-4 w-full max-w-xl pt-2">
            <Button
              onClick={() => handleChoice(true)}
              className="flex-1 text-[28px] !py-4 !px-6"
              textColor="methadone"
            >
              {language === 'id' ? `Ya (+${priceLabel})` : `Yes (+${priceLabel})`}
            </Button>
            <Button
              onClick={() => handleChoice(false)}
              className="flex-1 text-[28px] !py-4 !px-6"
              textColor="methadone"
            >
              {language === 'id' ? 'Tidak, foto saja' : 'No, photos only'}
            </Button>
          </div>

          <p className="text-[16px] text-white/75 font-body-bahela">
            {language === 'id'
              ? 'Pilihan ini menentukan harga QRIS dan apakah video akan dibuat.'
              : 'This choice sets the QR amount and whether a video is created.'}
          </p>
        </div>
      </div>
    </div>
  )
}

export default RecordingChoiceModal
